import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'type' | 'aria-label'
> {
  /** Required: the box carries no visible label of its own. */
  'aria-label': string;
  /** Header state when only some rows are selected; set through the DOM
   * property because the attribute does not exist in HTML. */
  indeterminate?: boolean;
}

/**
 * Native checkbox in the primary colour with the same focus ring as the
 * Switch. Stays a plain `<input>` (no Radix dependency) so it carries the
 * browser's own keyboard handling and form semantics.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate = false, ...props }, ref) => {
    const inner = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle(ref, () => inner.current!, []);

    React.useEffect(() => {
      if (inner.current) inner.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
      <input
        ref={inner}
        type="checkbox"
        className={cn(
          'h-4 w-4 shrink-0 cursor-pointer accent-primary',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
          'disabled:cursor-not-allowed disabled:opacity-40',
          className
        )}
        {...props}
      />
    );
  }
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
